import React, { useState, useEffect, useRef } from "react";
import {
    MagnifyingGlassIcon,
    ClockIcon,
    CheckCircleIcon,
    PaperAirplaneIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAdminChats, useAdminChat, useReplyMessage } from "../../lib/adminChatQueries";
import { canAccess } from "../../utils/permissions";

export default function AdminLiveChatDashboard() {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedChat, setSelectedChat] = useState(null);
    const [replyText, setReplyText] = useState("");
    const messagesEndRef = useRef(null);

    const { data: chats = [], isLoading } = useAdminChats();
    const { data: activeChat, isLoading: chatLoading } = useAdminChat(selectedChat?.id);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [activeChat?.messages]);
    const replyMutation = useReplyMessage();

    if (!canAccess("live_chat")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <ClockIcon className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to access live chat.
                </p>
            </div>
        );
    }

    const filteredChats = chats
        .filter((chat) =>
            (chat.user?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (chat.user?.email || "").toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            const aHasUnreplied = a.messages?.some((m) => m.type === "user" && m.status !== "replied");
            const bHasUnreplied = b.messages?.some((m) => m.type === "user" && m.status !== "replied");

            if (aHasUnreplied && !bHasUnreplied) return -1;
            if (!aHasUnreplied && bHasUnreplied) return 1;

            // Secondary sort: most recent first
            return new Date(b.created_at) - new Date(a.created_at);
        });

    const pendingChats = chats.filter((chat) =>
        chat.messages?.some((m) => m.type === "user" && m.status !== "replied")
    ).length;

    const handleSendReply = () => {
        const userMessages = activeChat?.messages?.filter((m) => m.type === "user") || [];
        const lastUserMessage = userMessages[userMessages.length - 1];

        if (!replyText.trim() || !lastUserMessage || replyMutation.isPending) return;

        replyMutation.mutate(
            {
                messageId: lastUserMessage.id,
                content: replyText,
                chatId: selectedChat.id,
            },
            {
                onSuccess: () => {
                    setReplyText("");
                },
            }
        );
    };

    return (
        <section className="space-y-8">
            {/* العنوان */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Live Chat Dashboard</h1>
                    <p className="text-gray-600 mt-1">Manage all customer support chats in real-time</p>
                </div>
                <div className="flex gap-4">
                    <span className="px-5 py-3 bg-orange-100 text-orange-800 rounded-xl text-sm font-bold flex items-center gap-2">
                        <ClockIcon className="w-6 h-6" />
                        {pendingChats} Pending Replies
                    </span>
                    <span className="px-5 py-3 bg-green-100 text-green-800 rounded-xl text-sm font-bold flex items-center gap-2">
                        <CheckCircleIcon className="w-6 h-6" />
                        {chats.length} Total Chats
                    </span>
                </div>
            </div>

            {/* البحث */}
            <div className="relative max-w-md">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-6 py-4 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1665f6] focus:border-[#1665f6] transition"
                />
            </div>

            {/* قائمة الشاتات */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* الشاتات */}
                <div className="lg:col-span-1 space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">Active Chats</h2>
                    {isLoading ? (
                        <div className="text-center py-10 text-gray-500">Loading chats...</div>
                    ) : filteredChats.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">No chats found</div>
                    ) : (
                        <div className="space-y-3">
                            {filteredChats.map((chat) => (
                                <div
                                    key={chat.id}
                                    onClick={() => {
                                        setSelectedChat(chat);
                                        setReplyText("");
                                    }}
                                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedChat?.id === chat.id
                                        ? "border-[#1665f6] bg-blue-50"
                                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-[#1665f6] to-[#8830b8] rounded-full flex items-center justify-center text-white font-bold">
                                                {chat.user?.name ? chat.user.name.split(" ").map(n => n[0]).join("").slice(0, 2) : "U"}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900">{chat.user?.name || "Unknown User"}</p>
                                                <p className="text-sm text-gray-600">{chat.user?.email || "No email"}</p>
                                                {chat.messages?.length > 0 && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {chat.messages[chat.messages.length - 1]?.content?.slice(0, 30)}...
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {chat.messages?.some(m => m.type === "user" && m.status !== "replied") && (
                                            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-3">
                                        {new Date(chat.created_at).toLocaleString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2">
                    {selectedChat ? (
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col h-[80vh]">
                            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-t-2xl">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold">
                                        {selectedChat.user?.name ? selectedChat.user.name.split(" ").map(n => n[0]).join("").slice(0, 2) : "U"}
                                    </div>
                                    <div>
                                        <p className="text-xl font-bold">{selectedChat.user?.name || "Unknown User"}</p>
                                        <p className="text-sm opacity-90">{selectedChat.user?.email || "No email"}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedChat(null)}
                                    className="p-2 hover:bg-white/20 rounded-lg transition"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {chatLoading ? (
                                    <div className="text-center text-gray-500">Loading messages...</div>
                                ) : !activeChat?.messages || activeChat.messages.length === 0 ? (
                                    <div className="text-center text-gray-500">No messages yet</div>
                                ) : (
                                    activeChat.messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                className={`max-w-lg rounded-2xl px-6 py-4 shadow-lg ${msg.type === "user"
                                                    ? "bg-gradient-to-br from-[#1665f6] to-[#8830b8] text-white"
                                                    : "bg-gray-100 text-gray-800"
                                                    }`}
                                            >
                                                <p className="text-sm leading-relaxed">{msg?.content}</p>
                                                <p className={`text-xs mt-2 ${msg?.type === "user" ? "text-white/70" : "text-gray-500"}`}>
                                                    {msg?.created_at ? new Date(msg.created_at).toLocaleTimeString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    }) : ""}
                                                    {msg?.status === "replied" && " · Replied"}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* منطقة الكتابة */}
                            <div className="p-6 border-t border-gray-200">
                                <div className="flex gap-3">
                                    <textarea
                                        rows="3"
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendReply();
                                            }
                                        }}
                                        placeholder="Type your message..."
                                        className="flex-1 px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1665f6] resize-none"
                                    />
                                    <button
                                        onClick={handleSendReply}
                                        disabled={!replyText.trim() || !activeChat?.messages?.some(m => m.type === "user") || replyMutation.isPending}
                                        className="self-end px-8 py-4 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white font-bold rounded-xl shadow-lg hover:shadow-xl flex items-center gap-2 transition disabled:opacity-50"
                                    >
                                        <PaperAirplaneIcon className="w-5 h-5" />
                                        Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-[80vh] flex items-center justify-center text-gray-400 text-xl">
                            Select a chat to start replying
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

